<i18n>
{
  "en": {
    "createRecommendationTitle": "Create menu initial recommendation",
    "createRecommendationSubtitle": "Create a recommendation to be displayed on the menu.",
    "editRecommendationTitle": "Edit menu recommendation",
    "editRecommendationSubtitle": "Edit the recommendation to be displayed on the menu.",
    "title": "Recommendation text to show in menu",
    "default": "Default Recommendation",
    "image": "Select image from file",
    "selectAProduct": "Select a product",
    "selectAMenu": "Select a menu",
    "selectServices": "Select services",
    "typeToSearch": "Type to search",
    "visible": "Product is visible",
    "fileSizeError": "Image is too big. Maximum size is 2 MB.",
    "tooLongText": "Input text is too long",
    "emptyFieldError": "Required field",
    "create": "Add",
    "edit": "Edit",
    "confirm": "Confirm",
    "imageHint": "Image to show in the recommendation, it's ok if there is no image",
    "imageHintExisting": "Image to show in the recommendation. Warning, the current one will be overridden!",
    "previousFile": "Previous File",
    "validationError": "Fields values not valid",
    "confirmCreateTitle": "Are you sure you want to save this Recommendation?",
    "confirmEditTitle": "Are you sure you want to save this changes?",
    "confirmText": "This cannot be undone",
    "fileFormatError": "This format is not valid",
    "validationErrorMinimumOneField": "At least one of these fields is required: texts, product or image",
    "timeRangeTitle": "Selection of time range",
    "timeStart": "Time start",
    "timeEnd": "Time end",
    "errorTimeRange": "The final time cannot be less than the initial time"
  },
  "es": {
    "createRecommendationTitle": "Crea una recomendación inicial de menú",
    "createRecommendationSubtitle": "Crea una recomendación para que se muestren en el menú.",
    "editRecommendationTitle": "Edita una recomendación de menú",
    "editRecommendationSubtitle": "Edita la recomendación que se va a mostrar en el menú.",
    "title": "Texto de la recomendación a mostrar en el menú",
    "default": "Recomendación por defecto",
    "image": "Selecciona la imagen desde el explorador",
    "selectAProduct": "Selecciona un producto",
    "selectAMenu": "Selecciona un menú",
    "selectServices": "Selecciona los servicios",
    "typeToSearch": "Escribe para buscar",
    "visible": "El producto es visible",
    "fileSizeError": "La imagen es demasiado grande. El tamaño máximo es 2 MB.",
    "tooLongText": "El texto es demasiado largo",
    "emptyFieldError": "Campo obligatorio",
    "create": "Añadir",
    "edit": "Editar",
    "confirm": "Aceptar",
    "imageHint": "Imagen a mostrar en la recomendación. No es obligatoria",
    "imageHintExisting": "Image a mostrar en la recomendación. La actual será sobreescrita",
    "previousFile": "Imagen anterior",
    "validationError": "Valores no válidos en los campos",
    "confirmCreateTitle": "Está seguro de que desea guardar esta recomendación?",
    "confirmEditTitle": "Está seguro de que desea guardar estos cambios?",
    "confirmText": "Esto no se puede deshacer",
    "fileFormatError": "Este formato no es valido",
    "validationErrorMinimumOneField": "Rellenar al menos uno de los siguientes campos: textos, producto o imagen",
    "timeRangeTitle":  "Selección del intervalo de tiempo",
    "timeStart":  "Hora de inicio",
    "timeEnd":  "Hola de fin",
    "errorTimeRange":  "El tiempo final no puede ser inferior al tiempo inicial."
  }
}
</i18n>

<template>
  <v-dialog @click:outside="onCancel" v-model="visible" max-width="600px" scrollable persistent>
    <v-card v-if="visible" height="100%" class="px-5 py-4">
      <div v-if="!editMode" class="d-flex flex-column">
        <v-card-title class="word-break pa-0 mb-4">
          {{ $t('createRecommendationTitle') }}
        </v-card-title>
        <v-card-subtitle class="pa-0">{{ $t('createRecommendationSubtitle') }}</v-card-subtitle>
      </div>
      <div v-else class="d-flex flex-column">
        <v-card-title class="word-break pa-0 mb-4">
          {{ $t('editRecommendationTitle') }}
        </v-card-title>
        <v-card-subtitle class="pa-0">{{ $t('editRecommendationSubtitle') }}</v-card-subtitle>
      </div>
      <v-container class="pa-0">
        <dynameat-confirmation-modal :visible="confirmCreateModalVisible" :title="$t('confirmCreateTitle')"
                                     :description="$t('confirmText')" :confirm-text="$t('confirm')"
                                     @confirm="onConfirm" @cancel="onCloseConfirm"/>
        <dynameat-confirmation-modal :visible="confirmEditModalVisible" :title="$t('confirmEditTitle')"
                                     :description="$t('confirmText')" :confirm-text="$t('confirm')"
                                     @confirm="onConfirm" @cancel="onCloseConfirm"/>
        <v-form v-model="valid" ref="formModal">
          <DynameatLangugageFieldGenerator
            v-model="text"
            :label="$t('title')"
            :type="'textArea'"
            :rules="shortTextRulesOrEmpty"
          />
          <v-switch v-model="activeByDefault" :label="$t('default')"/>

          <!-- Week days selector -->
          <dynameat-select-days-week v-show="!activeByDefault" @days-selected="onDaysChange"
                                     :actualWeekDays="weekDict">
          </dynameat-select-days-week>

          <!-- Time range selector -->
          <fragment v-if="!activeByDefault">
            <p   class="mb-0 mt-3"> {{ $t('timeRangeTitle') }} <span class="error--text">*</span></p>
            <span class="error--text caption mb-1" v-if="!validEndTime">{{ $t('errorTimeRange') }}</span>
            <div class="d-flex flex-row">
              <div class="mr-3">
                <label class="d-block v-card__subtitle m-0 px-0 py-0">{{ $t('timeStart') }}</label>
                <date-picker  class="time-picker" mode="time" v-model="timeStart" is24hr/>
              </div>
              <div>
                <label class="d-block v-card__subtitle m-0 px-0 py-0">{{ $t('timeEnd') }}</label>
                <date-picker class="time-picker" mode="time" v-model="timeEnd" is24hr/>
              </div>
            </div>
          </fragment>

          <v-select v-model="menu" :items="menuList" :menu-props="{ maxHeight: '400' }"
                    :label="$t('selectAMenu')" :rules="objectRules" item-value="guid"
                    item-text="title" chips class="required"/>

          <!-- Product selector -->
          <dynameat-autocomplete v-model="product" :items="availableProductsList" @change="onChange"
                                 :placeholder="$t('typeToSearch')" prepend-icon="mdi-database-search"
                                 item-text="name" chips persistent-hint item-value="guid"
                                 :label="$t('selectAProduct')" :filter="onProductTextFilter"
                                 >
            <template v-slot:item="data">
              <template>
                <v-list-item-avatar>
                  <v-img :src="data.item.image" :alt="data.item.name"
                         :lazy-src="defaultImg" contain/>
                </v-list-item-avatar>
                <v-list-item-content class="h-scroll">
                  <v-list-item-title class="no-wrap">
                    {{ data.item.commercial_name }}
                    (
                    {{currency.hasLeftPosition() ? currency.symbol : ''}}
                    {{ data.item.first_price }}
                    {{currency.hasRightPosition() ? currency.symbol : ''}}
                    )
                  </v-list-item-title>
                  <v-list-item-subtitle class="no-wrap">
                    {{ data.item.name }} ({{ data.item.id_external }})
                  </v-list-item-subtitle>
                </v-list-item-content>
              </template>
            </template>
            <template v-slot:selection="data">
              <v-chip class="caption | d-flex justify-center align-center" close
                      @click:close="onRemoveProduct" @click.stop>
                <div class="chip-ellipsis">{{ data.item.name }}</div>
              </v-chip>
            </template>
          </dynameat-autocomplete>

          <!-- Image selector -->
          <v-file-input v-model="image" :rules="imageRules" :show-size="showImageSize"
                        accept="image/png, image/jpeg, image/bmp" prepend-inner-icon="mdi-camera"
                        prepend-icon="" persistent-hint :hint="imageHint" :label="$t('image')"
                        class="mb-6"/>

          <dynameat-modal-buttons @cancel="onCancel" @confirm="onOk" :confirmDisabled="!isValid"
                                  :confirmText="$t('confirm')"/>
          <!-- Master Product modal -->
          <master-product-modal :visible="masterProductModalVisible" :product-prop="masterProductToEdit"
                                @close="onEditMasterProduct"/>
        </v-form>
      </v-container>
    </v-card>
  </v-dialog>
</template>

<script>

import Component from 'vue-class-component';
import {
  Components, MapActions, Mixins, MapGetters
} from '@core/helpers/decorators';
import DynameatModalButtons from '@dynameat/components/DynameatModalButtons';
import { Prop, Watch } from 'vue-property-decorator';
import Vue from 'vue';
import DynameatAutocomplete from '@dynameat/components/DynameatAutocomplete';
import {
  maxChars, maxSize, notEmptyList, notEmptyObject, imageFileType
} from '@core/helpers/formValidators';
import DynameatSelectDaysWeek from '@dynameat/components/DynameatSelectDaysWeek';
import { createErrorNotification } from '@ulibs/unnotificationsqueue';
import MasterProductModal from '@masters/views/product/components/MasterProductModal.vue';
import DynameatConfirmationModal from '@dynameat/components/DynameatConfirmationModal';
import { productTextFilter } from '@core/mixins';
import { productMixins } from '@pointOfSale/mixins';
import DynameatLangugageFieldGenerator from '@dynameat/components/DynameatLanguageFieldGenerator';
import DatePicker from 'v-calendar/lib/components/date-picker.umd';
import { getTimeFormat } from '@core/helpers';
import { MenuInitialRecommendation } from '../models';

@Component
@Components({
  DynameatModalButtons,
  DynameatAutocomplete,
  DynameatSelectDaysWeek,
  DynameatConfirmationModal,
  MasterProductModal,
  DynameatLangugageFieldGenerator,
  DatePicker
})
@Mixins([productMixins])
@MapActions(['addNotification'])
@MapGetters(['pointOfSaleLanguages', 'currency'])
export default class RecommendationCreateEditModal extends Vue {
  @Prop({ Boolean, default: false }) editMode;

  @Prop({
    Object,
    validator(recommendation) {
      return recommendation instanceof MenuInitialRecommendation;
    },
  }) recommendation;

  @Prop({ Boolean, default: false }) visible;

  @Prop({ type: Array, default: [] }) menuList;

  @Prop({ type: String, default: 'Recomendación 1' }) defaultRecommendationTitle;

  title = '';

  text = {}

  activeByDefault = false;

  product = null;

  menu = null;

  timeStart = new Date()

  timeEnd = new Date()

  image = null;

  valid = false;

  weekDict = {
    monday: false,
    tuesday: false,
    wednesday: false,
    thursday: false,
    friday: false,
    saturday: false,
    sunday: false,
  }

  confirmCreateModalVisible = false;

  confirmEditModalVisible = false;

  masterProductModalVisible = false;

  masterProductToEdit = null;

  @Watch('visible')
  async onVisibleChange() {
    this.clearOwnData();
  }

  get defaultImg() {
    // eslint-disable-next-line global-require
    return require('@dynameat/assets/images/dynameat-logo-secondary.png');
  }

  get imageRules() {
    return [maxSize(this.$t('fileSizeError'), 2000000), imageFileType(this.$t('fileFormatError'))];
  }

  get objectRules() {
    return [notEmptyObject(this.$t('emptyFieldError'))];
  }

  get isValid() {
    return (this.valid && Object.values(this.weekDict).some((weekday) => weekday)) || (this.valid && this.activeByDefault);
  }

  get shortTextRulesOrEmpty() {
    return {
      alt1: [maxChars(this.$t('tooLongText'), 128)],
      alt2: [maxChars(this.$t('tooLongText'), 128)],
      alt3: [[maxChars(this.$t('tooLongText'), 128)]]
    };
  }

  get imageHint() {
    if (this.image) {
      return this.$t('imageHintExisting');
    }
    return this.$t('imageHint');
  }

  get showImageSize() {
    return this.image instanceof File && this.image.size > 0;
  }

  get availableLanguages() {
    return this.pointOfSaleLanguages;
  }

  get emptyMainText() {
    let emptyText = true;
    if (this.text.alt1) {
      emptyText = this.text.alt1.trim() === '';
    }
    return emptyText;
  }

  get validEndTime() {
    const horusTimeEnd = this.timeEnd.getHours();
    const minutesTimeEnd = this.timeEnd.getMinutes();
    const hoursTimeStart = this.timeStart.getHours();
    const minutesTimeStart = this.timeStart.getMinutes();

    if (horusTimeEnd === hoursTimeStart) {
      if (minutesTimeEnd >= minutesTimeStart) {
        return true;
      }
    }

    if (horusTimeEnd > hoursTimeStart) {
      if (minutesTimeEnd > minutesTimeStart) {
        return true;
      }
      return true;
    }

    return false;
  }

  onProductTextFilter = productTextFilter

  onCancel() {
    this.$emit('cancel');
    this.clearOwnData();
  }

  onCloseConfirm() {
    this.confirmCreateModalVisible = false;
    this.confirmEditModalVisible = false;
  }

  onOpenCreateConfirm() {
    this.confirmCreateModalVisible = true;
  }

  onOpenEditConfirm() {
    this.confirmEditModalVisible = true;
  }

  onOk() {
    this.$refs.form?.validate();
    if (this.valid) {
      if (this.image || this.product || this.text.alt1) {
        if (!this.editMode) this.onOpenCreateConfirm();
        else this.onOpenEditConfirm();
      } else {
        this.addNotification(createErrorNotification(this.$t('validationErrorMinimumOneField')));
      }
    } else {
      this.addNotification(createErrorNotification(this.$t('validationError')));
    }
  }

  onConfirm() {
    if (this.$refs.formModal.validate()) {
      this.title = this.emptyMainText ? this.defaultRecommendationTitle : this.text.alt1;
      const recommendation = new MenuInitialRecommendation(
        this.recommendation?.guid || null,
        this.title,
        this.text.alt1,
        this.text.alt2,
        this.text.alt3,
        this.image,
        this.activeByDefault,
        this.menu,
        this.product,
        this.activeByDefault ? null : getTimeFormat(this.timeStart),
        this.activeByDefault ? null : getTimeFormat(this.timeEnd),
        this.weekDict.monday,
        this.weekDict.tuesday,
        this.weekDict.wednesday,
        this.weekDict.thursday,
        this.weekDict.friday,
        this.weekDict.saturday,
        this.weekDict.sunday,
      );

      this.confirmCreateModalVisible = false;
      this.confirmEditModalVisible = false;

      if (!this.editMode) this.$emit('confirm-create', recommendation);
      else this.$emit('confirm-edit', recommendation);
    }
  }

  // Chips
  onRemoveProduct() {
    this.product = null;
  }

  onDaysChange(newDict) {
    this.weekDict = newDict;
  }

  onChange(event) {
    const productFined = this.availableProductsList.filter((product) => product.guid === event);
    if (productFined.length > 0) {
      const product = productFined[0];
      if (product.is_new || !product.isConfigured) {
        this.masterProductToEdit = product;
        this.masterProductModalVisible = true;
      }
    }
  }

  onEditMasterProduct(product) {
    if (!product) {
      this.product = null;
    }
    this.masterProductModalVisible = false;
    this.masterProductToEdit = null;
  }

  clearOwnData() {
    this.title = '';
    this.text = {};
    this.activeByDefault = false;
    this.product = null;
    this.menu = null;
    this.image = null;
    this.weekDict = {
      monday: false,
      tuesday: false,
      wednesday: false,
      thursday: false,
      friday: false,
      saturday: false,
      sunday: false,
    };
  }

  @Watch('recommendation', { immediate: true })
  onRecommendationChanged(updatedRecommendation) {
    if (updatedRecommendation) {
      this.clearOwnData();
      this.title = updatedRecommendation.title;
      this.availableLanguages.forEach((el) => {
        this.text[el.alternative] = updatedRecommendation[`text${el.alternative.charAt(0).toUpperCase() + el.alternative.slice(1)}`];
      });
      this.activeByDefault = updatedRecommendation.activeByDefault;
      this.product = updatedRecommendation.product;
      this.menu = updatedRecommendation.menu;
      this.image = updatedRecommendation.image ? new File([], this.$t('previousFile')) : null;
      this.weekDict = {
        monday: updatedRecommendation.monday,
        tuesday: updatedRecommendation.tuesday,
        wednesday: updatedRecommendation.wednesday,
        thursday: updatedRecommendation.thursday,
        friday: updatedRecommendation.friday,
        saturday: updatedRecommendation.saturday,
        sunday: updatedRecommendation.sunday,
      };
      this.timeStart = updatedRecommendation.timeStart ? new Date(`1970-01-01T${updatedRecommendation.timeStart}`) : new Date();
      this.timeEnd = updatedRecommendation.timeEnd ? new Date(`1970-01-01T${updatedRecommendation.timeEnd}`) : new Date();
    }
  }

  @Watch('activeByDefault')
  async onRouteChange(oldActive, newActive) {
    if (!!oldActive && !newActive) {
      this.weekDict = {
        monday: false,
        tuesday: false,
        wednesday: false,
        thursday: false,
        friday: false,
        saturday: false,
        sunday: false
      };
    }
  }
}
</script>

<style>
.required label::after {
  content: " *";
  color: red;
}
.time-picker .vc-date {
  display: None !important;
}
</style>
